import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Seo from '../components/Seo';
import { seo } from '../seo/seoData';
import { useNavigate, Link } from 'react-router-dom';
import { Building2, Mail, Lock, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';

const API_URL = (import.meta.env.VITE_CORPORATE_URL || import.meta.env.VITE_BACKEND_URL) + '/api';

const CorporateLoginPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_URL}/employees/login`, formData);
      
      // Store token and employee info
      localStorage.setItem('employeeToken', response.data.access_token);
      localStorage.setItem('employeeInfo', JSON.stringify(response.data.employee));
      
      navigate('/company/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Identifiants invalides');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#2B2B2B] flex items-center justify-center px-4" data-testid="corporate-login-page">
          <Seo {...seo.company} />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-black border border-white/10 rounded-2xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-white mb-2">
              Espace Entreprise
            </h1>
            <p className="text-white/50 text-sm">
              Connectez-vous pour gérer vos réservations
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl mb-6 text-sm"
            >
              {error}
            </motion.div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-neutral-400">
                Email professionnel
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="contact@entreprise.fr"
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-neutral-500 focus:border-white/20 rounded-xl"
                  required
                  data-testid="corporate-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-neutral-400">
                Mot de passe
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="••••••••"
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-neutral-500 focus:border-white/20 rounded-xl"
                  required
                  data-testid="corporate-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-black text-white border border-white/15 hover:bg-white/5 hover:border-white/25 py-6 text-lg font-medium rounded-xl transition-all"
              data-testid="corporate-login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Connexion...
                </>
              ) : (
                'Se connecter'
              )}
            </Button>
          </form>

          {/* Links */}
          <div className="mt-6 pt-6 border-t border-white/10 text-center">
            <p className="text-neutral-500 text-sm">
              Pas encore de compte entreprise ?{' '}
              <Link to="/entreprises" className="text-white hover:underline">
                Nous contacter
              </Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default CorporateLoginPage;
